import { useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import advancedSearchFilteringTrackingService from "../services/tracking";
import { setIsFiltersCategoriesPanelExpanded, setSelectedFilterCategory } from "../store/action-creators";
import { selectIsFiltersCategoriesPanelExpanded, selectSelectedFilterCategory } from "../store/selectors";
import type { FilterCategory } from "../types/category";

type HookValue = {
    selectedCategory: FilterCategory;
    isCategoryPanelExpanded: boolean;
    onChangeCategory: (category: FilterCategory) => void;
    onToggleCategoryPanel: VoidFunction;
    onCloseCategoryPanel: VoidFunction;
    onOpenCategoryPanel: (category: FilterCategory) => void;
};

export const useFilterCategoryPanelState = (): HookValue => {
    const dispatch = useDispatch();

    const selectedFilterCategory = useSelector(selectSelectedFilterCategory);
    const isFiltersCategoriesPanelExpanded = useSelector(selectIsFiltersCategoriesPanelExpanded);

    const onChangeFilterCategory = useCallback((category: FilterCategory) => {
        advancedSearchFilteringTrackingService.filtersPanel.trackChangeFiltersTab(category);

        dispatch(setSelectedFilterCategory(category));
    }, []);

    const onToggleCategoryPanel = useCallback(() => {
        if (isFiltersCategoriesPanelExpanded) {
            advancedSearchFilteringTrackingService.filtersPanel.trackShowLessFilters();
        } else {
            advancedSearchFilteringTrackingService.filtersPanel.trackShowMoreFilters();
        }

        dispatch(setIsFiltersCategoriesPanelExpanded(!isFiltersCategoriesPanelExpanded));
    }, [isFiltersCategoriesPanelExpanded]);

    const onCloseCategoryPanel = useCallback(() => {
        if (!isFiltersCategoriesPanelExpanded) {
            return;
        }

        advancedSearchFilteringTrackingService.filtersPanel.trackShowLessFilters();
        dispatch(setIsFiltersCategoriesPanelExpanded(false));
    }, [isFiltersCategoriesPanelExpanded]);

    const onOpenCategoryPanel = useCallback((category: FilterCategory) => {
        advancedSearchFilteringTrackingService.filtersPanel.trackCategoryViewMore(category);

        dispatch(setSelectedFilterCategory(category));
        dispatch(setIsFiltersCategoriesPanelExpanded(true));
    }, []);

    return {
        selectedCategory: selectedFilterCategory,
        isCategoryPanelExpanded: isFiltersCategoriesPanelExpanded,
        onChangeCategory: onChangeFilterCategory,
        onToggleCategoryPanel,
        onCloseCategoryPanel,
        onOpenCategoryPanel,
    };
};
